/**
 * EmailTemplatesPage — manage vault_email_templates (shared with
 * Fias 360 in the same Holder). Categories filter the list; render
 * dialog substitutes {{variables}} for preview.
 */

import { useState } from 'react';
import { Plus, Eye, Archive } from 'lucide-react';
import { api } from '@/lib/api';
import {
  useFiasLeadsEmailTemplates,
  useCreateFiasLeadsEmailTemplate,
  useUpdateFiasLeadsEmailTemplate,
  useArchiveFiasLeadsEmailTemplate,
} from '../hooks/useFiasLeads';
import type { FiasLeadsEmailTemplateCategory, FiasLeadsEmailTemplateDto } from '@/lib/api';
import { SkeletonRow } from '../components/Skeletons';
import { toast } from '@/components/ui/use-toast';
import { useConfirm } from '@/components/ui/confirm-dialog';

interface Props {
  holderId: string;
}

const CATEGORIES: FiasLeadsEmailTemplateCategory[] = [
  'sales_outreach',
  'customer_success',
  'support',
  'general',
];

const CATEGORY_COLORS: Record<FiasLeadsEmailTemplateCategory, string> = {
  sales_outreach: 'bg-blue-100 text-blue-700',
  customer_success: 'bg-emerald-100 text-emerald-700',
  support: 'bg-amber-100 text-amber-700',
  general: 'bg-slate-100 text-slate-700',
};

export function EmailTemplatesPage({ holderId }: Props) {
  const confirm = useConfirm();
  const [categoryFilter, setCategoryFilter] = useState<FiasLeadsEmailTemplateCategory | ''>('');
  const [showCreate, setShowCreate] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<FiasLeadsEmailTemplateDto | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<FiasLeadsEmailTemplateDto | null>(null);

  const [draft, setDraft] = useState<{
    name: string;
    subject: string;
    bodyMarkdown: string;
    category: FiasLeadsEmailTemplateCategory;
  }>({ name: '', subject: '', bodyMarkdown: '', category: 'sales_outreach' });

  const templatesQuery = useFiasLeadsEmailTemplates(holderId, {
    category: categoryFilter || undefined,
  });
  const createTemplate = useCreateFiasLeadsEmailTemplate(holderId);
  const updateTemplate = useUpdateFiasLeadsEmailTemplate(holderId);
  const archiveTemplate = useArchiveFiasLeadsEmailTemplate(holderId);

  const templates = templatesQuery.data ?? [];

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!draft.name.trim() || !draft.subject.trim()) return;
    const detectedVars = extractVariables(`${draft.subject} ${draft.bodyMarkdown}`);
    await createTemplate.mutateAsync({
      name: draft.name.trim(),
      subject: draft.subject.trim(),
      bodyMarkdown: draft.bodyMarkdown.trim() || undefined,
      variables: detectedVars,
      category: draft.category,
    });
    setDraft({ name: '', subject: '', bodyMarkdown: '', category: 'sales_outreach' });
    setShowCreate(false);
  };

  return (
    <div className="p-6 max-w-5xl mx-auto flex flex-col gap-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Email templates</h1>
          <p className="text-sm text-muted-foreground">
            Reusable templates with <code className="text-xs">{'{{variable}}'}</code> substitution.
            Shared with Fias 360 in the same Holder.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={categoryFilter}
            onChange={(e) =>
              setCategoryFilter(e.target.value as FiasLeadsEmailTemplateCategory | '')
            }
            className="rounded border border-border bg-card px-3 py-2 text-sm"
          >
            <option value="">All categories</option>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c.replace('_', ' ')}
              </option>
            ))}
          </select>
          <button
            onClick={() => setShowCreate((v) => !v)}
            className="inline-flex items-center gap-2 rounded bg-primary px-3 py-2 text-primary-foreground text-sm"
          >
            <Plus className="h-4 w-4" />
            New template
          </button>
        </div>
      </header>

      {showCreate && (
        <form
          onSubmit={handleCreate}
          className="rounded border border-border bg-card p-4 flex flex-col gap-2"
        >
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="Template name *"
              value={draft.name}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
              className="flex-1 rounded border border-border bg-background px-3 py-2 text-sm"
              required
            />
            <select
              value={draft.category}
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  category: e.target.value as FiasLeadsEmailTemplateCategory,
                }))
              }
              className="rounded border border-border bg-background px-3 py-2 text-sm"
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c.replace('_', ' ')}
                </option>
              ))}
            </select>
          </div>
          <input
            type="text"
            placeholder="Subject *  (use {{variables}})"
            value={draft.subject}
            onChange={(e) => setDraft((d) => ({ ...d, subject: e.target.value }))}
            className="rounded border border-border bg-background px-3 py-2 text-sm"
            required
          />
          <textarea
            placeholder="Body markdown  (use {{variables}})"
            value={draft.bodyMarkdown}
            onChange={(e) => setDraft((d) => ({ ...d, bodyMarkdown: e.target.value }))}
            rows={6}
            className="rounded border border-border bg-background px-3 py-2 text-sm font-mono"
          />
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={!draft.name.trim() || !draft.subject.trim() || createTemplate.isPending}
              className="rounded bg-primary px-3 py-1.5 text-primary-foreground text-sm disabled:opacity-50"
            >
              {createTemplate.isPending ? 'Creating…' : 'Create'}
            </button>
            <button
              type="button"
              onClick={() => setShowCreate(false)}
              className="rounded border border-border px-3 py-1.5 text-sm"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {templatesQuery.isLoading && <SkeletonRow />}

      <div className="rounded border border-border bg-card">
        <ul className="divide-y divide-border">
          {templates.map((t) => (
            <li key={t.templateId} className="p-3 flex items-start gap-3 group">
              <span
                className={`shrink-0 rounded px-2 py-0.5 text-xs ${CATEGORY_COLORS[t.category]}`}
              >
                {t.category.replace('_', ' ')}
              </span>
              <div className="flex-1 min-w-0">
                <button
                  onClick={() => setSelectedTemplate(t)}
                  className="text-sm font-medium hover:underline text-left"
                >
                  {t.name}
                </button>
                <p className="text-xs text-muted-foreground mt-0.5 truncate">{t.subject}</p>
                {t.variables.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Variables: {t.variables.join(', ')}
                  </p>
                )}
              </div>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100">
                <button
                  onClick={() => setPreviewTemplate(t)}
                  className="rounded border border-border p-1 hover:bg-muted/30"
                  title="Preview"
                >
                  <Eye className="h-3 w-3" />
                </button>
                <button
                  onClick={async () => {
                    if (
                      await confirm({
                        title: `Archive "${t.name}"?`,
                        confirmLabel: 'Archive template',
                        destructive: true,
                      })
                    ) {
                      archiveTemplate.mutateAsync(t.templateId);
                    }
                  }}
                  className="rounded border border-border p-1 hover:bg-muted/30 hover:text-destructive"
                  title="Archive"
                >
                  <Archive className="h-3 w-3" />
                </button>
              </div>
            </li>
          ))}
        </ul>
        {templates.length === 0 && !templatesQuery.isLoading && (
          <p className="p-6 text-center text-muted-foreground text-sm">No templates yet.</p>
        )}
      </div>

      {selectedTemplate && (
        <EditTemplateModal
          template={selectedTemplate}
          onClose={() => setSelectedTemplate(null)}
          onSave={async (input) => {
            await updateTemplate.mutateAsync({
              templateId: selectedTemplate.templateId,
              input,
            });
            setSelectedTemplate(null);
          }}
        />
      )}

      {previewTemplate && (
        <PreviewModal
          holderId={holderId}
          template={previewTemplate}
          onClose={() => setPreviewTemplate(null)}
        />
      )}
    </div>
  );
}

function extractVariables(text: string): string[] {
  const out = new Set<string>();
  for (const m of text.matchAll(/\{\{\s*(\w+)\s*\}\}/g)) {
    out.add(m[1]);
  }
  return [...out];
}

function EditTemplateModal({
  template,
  onClose,
  onSave,
}: {
  template: FiasLeadsEmailTemplateDto;
  onClose: () => void;
  onSave: (input: {
    name: string;
    subject: string;
    bodyMarkdown: string;
    variables: string[];
    category: FiasLeadsEmailTemplateCategory;
  }) => Promise<void>;
}) {
  const [draft, setDraft] = useState({
    name: template.name,
    subject: template.subject,
    bodyMarkdown: template.bodyMarkdown,
    category: template.category,
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave({
        ...draft,
        variables: extractVariables(`${draft.subject} ${draft.bodyMarkdown}`),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4">
      <form
        onSubmit={submit}
        className="w-full max-w-2xl rounded border border-border bg-card p-6 flex flex-col gap-3"
      >
        <h2 className="text-lg font-semibold">Edit template</h2>
        <div className="flex gap-2">
          <input
            type="text"
            value={draft.name}
            onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
            className="flex-1 rounded border border-border bg-background px-3 py-2 text-sm"
            required
          />
          <select
            value={draft.category}
            onChange={(e) =>
              setDraft((d) => ({
                ...d,
                category: e.target.value as FiasLeadsEmailTemplateCategory,
              }))
            }
            className="rounded border border-border bg-background px-3 py-2 text-sm"
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c.replace('_', ' ')}
              </option>
            ))}
          </select>
        </div>
        <input
          type="text"
          value={draft.subject}
          onChange={(e) => setDraft((d) => ({ ...d, subject: e.target.value }))}
          className="rounded border border-border bg-background px-3 py-2 text-sm"
          required
        />
        <textarea
          value={draft.bodyMarkdown}
          onChange={(e) => setDraft((d) => ({ ...d, bodyMarkdown: e.target.value }))}
          rows={10}
          className="rounded border border-border bg-background px-3 py-2 text-sm font-mono"
        />
        <div className="flex gap-2 justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-border px-3 py-1.5 text-sm"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="rounded bg-primary px-3 py-1.5 text-primary-foreground text-sm disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
}

function PreviewModal({
  holderId,
  template,
  onClose,
}: {
  holderId: string;
  template: FiasLeadsEmailTemplateDto;
  onClose: () => void;
}) {
  const [values, setValues] = useState<Record<string, string>>(
    Object.fromEntries(template.variables.map((v) => [v, ''])),
  );
  const [rendered, setRendered] = useState<{ subject: string; bodyMarkdown: string } | null>(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await api.renderFiasLeadsEmailTemplate(holderId, template.templateId, values);
      setRendered(result);
    } catch (err) {
      toast({
        title: 'Render failed',
        description: (err as Error).message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4">
      <div className="w-full max-w-3xl rounded border border-border bg-card p-6 flex flex-col gap-3 max-h-[90vh] overflow-auto">
        <header className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Preview: {template.name}</h2>
          <button onClick={onClose} className="text-sm text-muted-foreground">
            Close
          </button>
        </header>

        {template.variables.length > 0 && (
          <form onSubmit={submit} className="flex flex-col gap-2">
            <p className="text-xs text-muted-foreground">Fill variables to render:</p>
            <div className="grid grid-cols-2 gap-2">
              {template.variables.map((v) => (
                <label key={v} className="flex flex-col gap-1">
                  <span className="text-xs text-muted-foreground">{v}</span>
                  <input
                    type="text"
                    value={values[v] ?? ''}
                    onChange={(e) => setValues((prev) => ({ ...prev, [v]: e.target.value }))}
                    className="rounded border border-border bg-background px-2 py-1 text-sm"
                  />
                </label>
              ))}
            </div>
            <button
              type="submit"
              disabled={loading}
              className="self-start rounded bg-primary px-3 py-1.5 text-primary-foreground text-sm disabled:opacity-50"
            >
              {loading ? 'Rendering…' : 'Render preview'}
            </button>
          </form>
        )}

        <div className="rounded border border-border bg-background p-3">
          <p className="text-xs text-muted-foreground">Subject</p>
          <p className="text-sm font-medium">{rendered?.subject ?? template.subject}</p>
        </div>
        <div className="rounded border border-border bg-background p-3">
          <p className="text-xs text-muted-foreground mb-1">Body</p>
          <pre className="text-sm whitespace-pre-wrap font-sans">
            {rendered?.bodyMarkdown ?? template.bodyMarkdown}
          </pre>
        </div>
      </div>
    </div>
  );
}
